import { useMutation } from "@tanstack/react-query";
import { useCallback, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchGraphQL } from "../../Graphql/utils";
import { OrderMutationRejectedError } from "./orderMutationErrors";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  API_RESPONSE_CODE,
  AlertBannerContext
} from "@kivunova/kivufrontendcommon";
import { RootState } from "../../store/store";

import {
  setSalesOrderList,
  setSalesOrderListMeta,
  setSalesOrderDetail,
  updateSalesOrderInList
} from "../../store/slices/salesOrderSlice";
import type { SalesOrderListMeta } from "../../store/slices/salesOrderSlice";
import {
  listOrdersQuery,
  listOrdersByStoreQuery,
  getOrderQuery,
  getOrderByPayloadQuery,
  createOrderMutation,
  addOrderItemMutation,
  updateOrderItemMutation,
  updateOrderItemDiscountMutation,
  removeOrderItemMutation,
  scanOrderItemMutation,
  confirmOrderItemMutation,
  cancelOrderMutation,
  acceptOfferMutation,
  updateOrderStatusMutation,
  completeOrderMutation,
  updateOrderCustomerMutation
} from "../../Graphql/querries/salesOrderQuery";

// Response types
import {
  Order,
  ScanResult,
  ListOrdersResponse,
  ListOrdersByStoreResponse,
  GetOrderResponse,
  GetOrderByPayloadResponse,
  CreateOrderResponse,
  AddOrderItemResponse,
  UpdateOrderItemResponse,
  UpdateOrderItemDiscountResponse,
  RemoveOrderItemResponse,
  ScanOrderItemResponse,
  ConfirmOrderItemResponse,
  CancelOrderResponse,
  AcceptOfferResponse,
  UpdateOrderStatusResponse,
  CompleteOrderResponse,
  UpdateOrderCustomerInput,
  UpdateOrderCustomerResponse
} from "../../OrderMFE/Orders/types/salesOrder";
// Input types
import { OrderStatus } from "../../OrderMFE/Orders/enums/orderStatus";
import {
  GetOrderInput,
  ListOrdersInput,
  CreateOrderInput,
  AddOrderItemInput,
  UpdateOrderItemInput,
  UpdateOrderItemDiscountInput,
  RemoveOrderItemInput,
  ScanOrderItemInput,
  ConfirmOrderItemInput,
  CancelOrderInput,
  AcceptOfferInput,
  UpdateOrderStatusInput,
  CompleteOrderInput
} from "../../OrderMFE/Orders/types/salesOrder";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

// ─── Sentinel error for already-handled API errors ─────────────────────────────

class HandledApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "HandledApiError";
  }
}

function buildListOrdersGraphqlInput(input: ListOrdersInput | undefined): {
  page: number;
  pageSize: number;
  status?: OrderStatus;
} {
  const page = input?.page ?? 0;
  const pageSize = input?.pageSize ?? 50;
  if (input?.status != null) {
    return { page, pageSize, status: input.status };
  }
  return { page, pageSize };
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

const useOrderDispatchers = () => {
  const dispatch = useDispatch();
  return {
    dispatchList: (orders: Order[], meta: SalesOrderListMeta | null) => {
      dispatch(setSalesOrderList(orders));
      dispatch(setSalesOrderListMeta(meta));
    },
    dispatchDetail: (order: Order | null) => {
      dispatch(setSalesOrderDetail(order));
      if (order) {
        dispatch(updateSalesOrderInList(order));
      }
    }
  };
};

// ─── List Orders ───────────────────────────────────────────────────────────────

export const useListOrders = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const { dispatchList } = useOrderDispatchers();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Order[], Error, ListOrdersInput | undefined>({
    mutationKey: ["listOrders"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const gqlInput = buildListOrdersGraphqlInput(input);
      const { page, pageSize } = gqlInput;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching order list",
        buildLogContext("orders", "list-orders", userId, companyId, undefined, {
          page: String(page),
          pageSize: String(pageSize),
          ...(gqlInput.status != null && { status: gqlInput.status })
        })
      );

      try {
        const response: ListOrdersResponse = await fetchGraphQL(
          listOrdersQuery,
          { input: gqlInput },
          setAlert
        );
        const row = response.listOrders;
        const { orders, code } = row;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch order list",
            buildLogContext(
              "orders",
              "list-orders",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code)
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return [];
        }

        if (!orders) {
          captureAppException(
            "API returned OK but orders array missing",
            new Error("API returned OK but orders array missing"),
            buildLogContext(
              "orders",
              "list-orders",
              userId,
              companyId,
              undefined
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return [];
        }

        const meta: SalesOrderListMeta = {
          totalCount: row.totalCount ?? orders.length,
          totalPages: row.totalPages ?? 1,
          page: row.page ?? page,
          pageSize: row.pageSize ?? pageSize
        };
        dispatchList(orders, meta);

        sendAppLog(
          LOG_LEVEL.INFO,
          "Order list fetched successfully",
          buildLogContext(
            "orders",
            "list-orders",
            userId,
            companyId,
            undefined,
            {
              orderCount: orders.length,
              totalCount: String(meta.totalCount)
            }
          )
        );
        return orders;
      } catch (error) {
        captureAppException(
          "Failed to fetch order list",
          error,
          buildLogContext("orders", "list-orders", userId, companyId, undefined)
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return [];
      }
    }
  });
};

// ─── List Orders By Store ──────────────────────────────────────────────────────

export const useListOrdersByStore = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const { dispatchList } = useOrderDispatchers();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Order[], Error, ListOrdersInput | undefined>({
    mutationKey: ["listOrdersByStore"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;
      const gqlInput = buildListOrdersGraphqlInput(input);
      const { page, pageSize } = gqlInput;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching store order list",
        buildLogContext(
          "orders",
          "list-orders-by-store",
          userId,
          companyId,
          storeId,
          {
            page: String(page),
            pageSize: String(pageSize),
            ...(gqlInput.status != null && { status: gqlInput.status })
          }
        )
      );

      try {
        const response: ListOrdersByStoreResponse = await fetchGraphQL(
          listOrdersByStoreQuery,
          { input: gqlInput },
          setAlert
        );
        const row = response.listOrdersByStore;
        const { orders, code } = row;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch store order list",
            buildLogContext(
              "orders",
              "list-orders-by-store",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(code)
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return [];
        }

        if (!orders) {
          captureAppException(
            "API returned OK but store orders array missing",
            new Error("API returned OK but store orders array missing"),
            buildLogContext(
              "orders",
              "list-orders-by-store",
              userId,
              companyId,
              storeId
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return [];
        }

        const meta: SalesOrderListMeta = {
          totalCount: row.totalCount ?? orders.length,
          totalPages: row.totalPages ?? 1,
          page: row.page ?? page,
          pageSize: row.pageSize ?? pageSize
        };
        dispatchList(orders, meta);

        sendAppLog(
          LOG_LEVEL.INFO,
          "Store order list fetched successfully",
          buildLogContext(
            "orders",
            "list-orders-by-store",
            userId,
            companyId,
            storeId,
            {
              orderCount: orders.length,
              totalCount: String(meta.totalCount)
            }
          )
        );
        return orders;
      } catch (error) {
        captureAppException(
          "Failed to fetch store order list",
          error,
          buildLogContext(
            "orders",
            "list-orders-by-store",
            userId,
            companyId,
            storeId
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return [];
      }
    }
  });
};

/**
 * Fetches every page of store orders for export without updating Redux.
 * Respects the same GraphQL filters as the list (page size + optional status).
 */
export const useFetchAllOrdersByStoreForExport = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useCallback(
    async (params: { pageSize: number; status?: OrderStatus }) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;
      const merged: Order[] = [];
      let page = 0;
      let totalPages = 1;

      while (page < totalPages) {
        const gqlInput = buildListOrdersGraphqlInput({
          page,
          pageSize: params.pageSize,
          ...(params.status != null ? { status: params.status } : {})
        });

        sendAppLog(
          LOG_LEVEL.INFO,
          "Fetching store order page for export",
          buildLogContext(
            "orders",
            "list-orders-by-store-export",
            userId,
            companyId,
            storeId,
            {
              page: String(page),
              pageSize: String(params.pageSize),
              ...(gqlInput.status != null && { status: gqlInput.status })
            }
          )
        );

        try {
          const response: ListOrdersByStoreResponse = await fetchGraphQL(
            listOrdersByStoreQuery,
            { input: gqlInput },
            setAlert
          );
          const row = response.listOrdersByStore;
          const { orders, code } = row;

          if (code !== API_RESPONSE_CODE.OK) {
            sendAppLog(
              LOG_LEVEL.WARNING,
              "Failed to fetch store orders for export",
              buildLogContext(
                "orders",
                "list-orders-by-store-export",
                userId,
                companyId,
                storeId,
                {
                  apiCode: String(code)
                }
              )
            );
            setAlert({
              severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
              code: code as unknown as ALERT_BANNER_CODE
            });
            throw new Error("Could not load orders for export");
          }

          if (!orders) {
            captureAppException(
              "API returned OK but store orders array missing (export)",
              new Error("API returned OK but store orders array missing"),
              buildLogContext(
                "orders",
                "list-orders-by-store-export",
                userId,
                companyId,
                storeId
              )
            );
            setAlert({
              severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
              code: ALERT_BANNER_CODE.INTERNAL_ERROR
            });
            throw new Error("Could not load orders for export");
          }

          merged.push(...orders);
          totalPages = Math.max(1, row.totalPages ?? 1);
          page += 1;

          if (orders.length === 0 && page > 1) {
            break;
          }
        } catch (error) {
          if (
            error instanceof Error &&
            error.message === "Could not load orders for export"
          ) {
            throw error;
          }
          captureAppException(
            "Failed to fetch store orders for export",
            error,
            buildLogContext(
              "orders",
              "list-orders-by-store-export",
              userId,
              companyId,
              storeId
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          throw error instanceof Error
            ? error
            : new Error("Could not load orders for export");
        }
      }

      sendAppLog(
        LOG_LEVEL.INFO,
        "Store order export fetch complete",
        buildLogContext(
          "orders",
          "list-orders-by-store-export",
          userId,
          companyId,
          storeId,
          {
            orderCount: String(merged.length)
          }
        )
      );

      return merged;
    },
    [setAlert, userDetails]
  );
};

// ─── Get Order ─────────────────────────────────────────────────────────────────

export const useGetOrder = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const { dispatchDetail } = useOrderDispatchers();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Order | null, Error, { orderId: string }>({
    mutationKey: ["getOrder"],
    mutationFn: async ({ orderId }) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching order detail",
        buildLogContext("orders", "get-order", userId, companyId, undefined, {
          orderId
        })
      );

      try {
        const response: GetOrderResponse = await fetchGraphQL(
          getOrderQuery,
          { orderId },
          setAlert
        );
        const { order, code } = response.getOrder;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch order detail",
            buildLogContext(
              "orders",
              "get-order",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!order) {
          captureAppException(
            "API returned OK but order object missing",
            new Error("API returned OK but order object missing"),
            buildLogContext(
              "orders",
              "get-order",
              userId,
              companyId,
              undefined,
              { orderId }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatchDetail(order);
        sendAppLog(
          LOG_LEVEL.INFO,
          "Order detail fetched successfully",
          buildLogContext("orders", "get-order", userId, companyId, undefined, {
            orderId: order.orderId
          })
        );
        return order;
      } catch (error) {
        captureAppException(
          "Failed to fetch order detail",
          error,
          buildLogContext("orders", "get-order", userId, companyId, undefined, {
            orderId
          })
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

// ─── Get Order By Payload ──────────────────────────────────────────────────────

export const useGetOrderByPayload = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const { dispatchDetail } = useOrderDispatchers();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Order | null, Error, GetOrderInput>({
    mutationKey: ["getOrderByPayload"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching order by payload",
        buildLogContext(
          "orders",
          "get-order-by-payload",
          userId,
          companyId,
          undefined,
          {
            orderId: input.orderId
          }
        )
      );

      try {
        const response: GetOrderByPayloadResponse = await fetchGraphQL(
          getOrderByPayloadQuery,
          { input },
          setAlert
        );
        const { order, code } = response.getOrderByPayload;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch order by payload",
            buildLogContext(
              "orders",
              "get-order-by-payload",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!order) {
          captureAppException(
            "API returned OK but order object missing",
            new Error("API returned OK but order object missing"),
            buildLogContext(
              "orders",
              "get-order-by-payload",
              userId,
              companyId,
              undefined,
              {
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatchDetail(order);
        sendAppLog(
          LOG_LEVEL.INFO,
          "Order fetched by payload successfully",
          buildLogContext(
            "orders",
            "get-order-by-payload",
            userId,
            companyId,
            undefined,
            {
              orderId: order.orderId
            }
          )
        );
        return order;
      } catch (error) {
        captureAppException(
          "Failed to fetch order by payload",
          error,
          buildLogContext(
            "orders",
            "get-order-by-payload",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

// ─── Create Order ──────────────────────────────────────────────────────────────

export const useCreateOrder = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const { dispatchDetail } = useOrderDispatchers();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Order, Error, CreateOrderInput>({
    mutationKey: ["createOrder"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started creating order",
        buildLogContext(
          "orders",
          "create-order",
          userId,
          companyId,
          undefined,
          {
            storeId: input.storeId as string,
            customerId: input.customerId as string
          }
        )
      );

      try {
        const response: CreateOrderResponse = await fetchGraphQL(
          createOrderMutation,
          { input },
          setAlert
        );
        const { order, code } = response.createOrder;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Order creation rejected by API",
            buildLogContext(
              "orders",
              "create-order",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code)
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          throw new HandledApiError(`Order creation rejected by API: ${code}`);
        }

        if (!order) {
          const err = new HandledApiError(
            "API returned OK but order object missing after createOrder"
          );
          captureAppException(
            "API returned OK but order object missing after createOrder",
            err,
            buildLogContext(
              "orders",
              "create-order",
              userId,
              companyId,
              undefined
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          throw err;
        }

        dispatchDetail(order);
        sendAppLog(
          LOG_LEVEL.INFO,
          "Order created successfully",
          buildLogContext(
            "orders",
            "create-order",
            userId,
            companyId,
            undefined,
            {
              orderId: order.orderId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });
        return order;
      } catch (error) {
        if (error instanceof HandledApiError) {
          throw error; // alert already set — don't overwrite it
        }
        captureAppException(
          "Failed to create order",
          error,
          buildLogContext(
            "orders",
            "create-order",
            userId,
            companyId,
            undefined
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw error;
      }
    }
  });
};

// ─── Order Item Mutations ──────────────────────────────────────────────────────

export type OrderItemMutationBannerOptions = {
  /**
   * When true, `code !== OK` throws {@link OrderMutationRejectedError} instead of
   * calling the global alert banner (e.g. add-item modal shows its own message).
   */
  suppressErrorBanner?: boolean;
};

export const useAddOrderItem = (options?: OrderItemMutationBannerOptions) => {
  const { setAlert } = useContext(AlertBannerContext);
  const { dispatchDetail } = useOrderDispatchers();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const suppressBanner = Boolean(options?.suppressErrorBanner);

  return useMutation<Order | null, Error, AddOrderItemInput>({
    mutationKey: ["addOrderItem", suppressBanner],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Adding item to order",
        buildLogContext(
          "orders",
          "add-order-item",
          userId,
          companyId,
          undefined,
          {
            orderId: input.orderId,
            itemId: input.itemId,
            quantity: input.quantity
          }
        )
      );

      try {
        const response: AddOrderItemResponse = await fetchGraphQL(
          addOrderItemMutation,
          { input },
          setAlert
        );
        const { order, code } = response.addOrderItem;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Add order item rejected by API",
            buildLogContext(
              "orders",
              "add-order-item",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                orderId: input.orderId
              }
            )
          );
          if (suppressBanner) {
            throw new OrderMutationRejectedError(String(code));
          }
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!order) {
          captureAppException(
            "API returned OK but order object missing after addOrderItem",
            new Error(
              "API returned OK but order object missing after addOrderItem"
            ),
            buildLogContext(
              "orders",
              "add-order-item",
              userId,
              companyId,
              undefined,
              {
                orderId: input.orderId
              }
            )
          );
          if (suppressBanner) {
            throw new OrderMutationRejectedError("MISSING_ORDER_PAYLOAD");
          }
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatchDetail(order);
        sendAppLog(
          LOG_LEVEL.INFO,
          "Order item added successfully",
          buildLogContext(
            "orders",
            "add-order-item",
            userId,
            companyId,
            undefined,
            {
              orderId: order.orderId,
              itemCount: order.items.length
            }
          )
        );
        return order;
      } catch (error) {
        if (error instanceof OrderMutationRejectedError) {
          throw error;
        }
        captureAppException(
          "Failed to add order item",
          error,
          buildLogContext(
            "orders",
            "add-order-item",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId
            }
          )
        );
        if (suppressBanner) {
          throw error instanceof Error ? error : new Error(String(error));
        }
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

// ─── Update Order Item ─────────────────────────────────────────────────────────

export const useUpdateOrderItem = (
  options?: OrderItemMutationBannerOptions
) => {
  const { setAlert } = useContext(AlertBannerContext);
  const { dispatchDetail } = useOrderDispatchers();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const suppressBanner = Boolean(options?.suppressErrorBanner);

  return useMutation<Order | null, Error, UpdateOrderItemInput>({
    mutationKey: ["updateOrderItem", suppressBanner],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Updating order item",
        buildLogContext(
          "orders",
          "update-order-item",
          userId,
          companyId,
          undefined,
          {
            orderId: input.orderId,
            productItemId: input.productItemId,
            quantity: input.quantity
          }
        )
      );

      try {
        const response: UpdateOrderItemResponse = await fetchGraphQL(
          updateOrderItemMutation,
          { input },
          setAlert
        );
        const { order, code } = response.updateOrderItem;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Update order item rejected by API",
            buildLogContext(
              "orders",
              "update-order-item",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                orderId: input.orderId
              }
            )
          );
          if (suppressBanner) {
            throw new OrderMutationRejectedError(String(code));
          }
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!order) {
          captureAppException(
            "API returned OK but order object missing after updateOrderItem",
            new Error(
              "API returned OK but order object missing after updateOrderItem"
            ),
            buildLogContext(
              "orders",
              "update-order-item",
              userId,
              companyId,
              undefined,
              {
                orderId: input.orderId
              }
            )
          );
          if (suppressBanner) {
            throw new OrderMutationRejectedError("MISSING_ORDER_PAYLOAD");
          }
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatchDetail(order);
        sendAppLog(
          LOG_LEVEL.INFO,
          "Order item updated successfully",
          buildLogContext(
            "orders",
            "update-order-item",
            userId,
            companyId,
            undefined,
            {
              orderId: order.orderId,
              productItemId: input.productItemId
            }
          )
        );
        return order;
      } catch (error) {
        if (error instanceof OrderMutationRejectedError) {
          throw error;
        }
        captureAppException(
          "Failed to update order item",
          error,
          buildLogContext(
            "orders",
            "update-order-item",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId
            }
          )
        );
        if (suppressBanner) {
          throw error instanceof Error ? error : new Error(String(error));
        }
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

export const useUpdateOrderItemDiscount = (
  options?: OrderItemMutationBannerOptions
) => {
  const { setAlert } = useContext(AlertBannerContext);
  const { dispatchDetail } = useOrderDispatchers();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const suppressBanner = Boolean(options?.suppressErrorBanner);

  return useMutation<Order | null, Error, UpdateOrderItemDiscountInput>({
    mutationKey: ["updateOrderItemDiscount", suppressBanner],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Updating order item discount",
        buildLogContext(
          "orders",
          "update-order-item-discount",
          userId,
          companyId,
          undefined,
          {
            orderId: input.orderId,
            productItemId: input.productItemId,
            discountPerUnit: input.discountPerUnit
          }
        )
      );

      try {
        const response: UpdateOrderItemDiscountResponse = await fetchGraphQL(
          updateOrderItemDiscountMutation,
          { input },
          setAlert
        );
        const { order, code } = response.updateOrderItemDiscount;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Update order item discount rejected by API",
            buildLogContext(
              "orders",
              "update-order-item-discount",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                orderId: input.orderId
              }
            )
          );
          if (suppressBanner) {
            throw new OrderMutationRejectedError(String(code));
          }
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!order) {
          captureAppException(
            "API returned OK but order object missing after updateOrderItemDiscount",
            new Error(
              "API returned OK but order object missing after updateOrderItemDiscount"
            ),
            buildLogContext(
              "orders",
              "update-order-item-discount",
              userId,
              companyId,
              undefined,
              {
                orderId: input.orderId
              }
            )
          );
          if (suppressBanner) {
            throw new OrderMutationRejectedError("MISSING_ORDER_PAYLOAD");
          }
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatchDetail(order);
        sendAppLog(
          LOG_LEVEL.INFO,
          "Order item discount updated successfully",
          buildLogContext(
            "orders",
            "update-order-item-discount",
            userId,
            companyId,
            undefined,
            {
              orderId: order.orderId,
              productItemId: input.productItemId
            }
          )
        );
        return order;
      } catch (error) {
        if (error instanceof OrderMutationRejectedError) {
          throw error;
        }
        captureAppException(
          "Failed to update order item discount",
          error,
          buildLogContext(
            "orders",
            "update-order-item-discount",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId
            }
          )
        );
        if (suppressBanner) {
          throw error instanceof Error ? error : new Error(String(error));
        }
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

// ─── Remove Order Item ─────────────────────────────────────────────────────────

export const useRemoveOrderItem = (
  options?: OrderItemMutationBannerOptions
) => {
  const { setAlert } = useContext(AlertBannerContext);
  const { dispatchDetail } = useOrderDispatchers();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const suppressBanner = Boolean(options?.suppressErrorBanner);

  return useMutation<Order | null, Error, RemoveOrderItemInput>({
    mutationKey: ["removeOrderItem", suppressBanner],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Removing item from order",
        buildLogContext(
          "orders",
          "remove-order-item",
          userId,
          companyId,
          undefined,
          {
            orderId: input.orderId,
            productItemId: input.productItemId
          }
        )
      );

      try {
        const response: RemoveOrderItemResponse = await fetchGraphQL(
          removeOrderItemMutation,
          { input },
          setAlert
        );
        const { order, code } = response.removeOrderItem;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Remove order item rejected by API",
            buildLogContext(
              "orders",
              "remove-order-item",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                orderId: input.orderId
              }
            )
          );
          if (suppressBanner) {
            throw new OrderMutationRejectedError(String(code));
          }
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!order) {
          captureAppException(
            "API returned OK but order object missing after removeOrderItem",
            new Error(
              "API returned OK but order object missing after removeOrderItem"
            ),
            buildLogContext(
              "orders",
              "remove-order-item",
              userId,
              companyId,
              undefined,
              {
                orderId: input.orderId
              }
            )
          );
          if (suppressBanner) {
            throw new OrderMutationRejectedError("MISSING_ORDER_PAYLOAD");
          }
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatchDetail(order);
        sendAppLog(
          LOG_LEVEL.INFO,
          "Order item removed successfully",
          buildLogContext(
            "orders",
            "remove-order-item",
            userId,
            companyId,
            undefined,
            {
              orderId: order.orderId,
              productItemId: input.productItemId
            }
          )
        );
        return order;
      } catch (error) {
        if (error instanceof OrderMutationRejectedError) {
          throw error;
        }
        captureAppException(
          "Failed to remove order item",
          error,
          buildLogContext(
            "orders",
            "remove-order-item",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId
            }
          )
        );
        if (suppressBanner) {
          throw error instanceof Error ? error : new Error(String(error));
        }
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

// ─── Scan / Confirm ────────────────────────────────────────────────────────────

export type ScanOrderItemOptions = {
  /** Called when the API returns a non-OK `code` (after the global banner is set). */
  onRejectedCode?: (code: string) => void;
};

export const useScanOrderItem = (options?: ScanOrderItemOptions) => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const onRejectedCode = options?.onRejectedCode;

  return useMutation<ScanResult | null, Error, ScanOrderItemInput>({
    mutationKey: ["scanOrderItem", Boolean(onRejectedCode)],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Scanning order item",
        buildLogContext(
          "orders",
          "scan-order-item",
          userId,
          companyId,
          undefined,
          {
            orderId: input.orderId,
            productItemId: input.productItemId
          }
        )
      );

      try {
        const response: ScanOrderItemResponse = await fetchGraphQL(
          scanOrderItemMutation,
          { input },
          setAlert
        );
        const { scan, code } = response.scanOrderItem;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Scan order item rejected by API",
            buildLogContext(
              "orders",
              "scan-order-item",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          onRejectedCode?.(String(code));
          return null;
        }

        if (!scan) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Scan order item OK but missing scan payload",
            buildLogContext(
              "orders",
              "scan-order-item",
              userId,
              companyId,
              undefined,
              {
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        sendAppLog(
          LOG_LEVEL.INFO,
          "Order item scanned successfully",
          buildLogContext(
            "orders",
            "scan-order-item",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId,
              scanType: scan.type
            }
          )
        );
        return scan;
      } catch (error) {
        captureAppException(
          "Failed to scan order item",
          error,
          buildLogContext(
            "orders",
            "scan-order-item",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

export const useConfirmOrderItem = (
  options?: OrderItemMutationBannerOptions
) => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const suppressBanner = Boolean(options?.suppressErrorBanner);

  return useMutation<ScanResult | null, Error, ConfirmOrderItemInput>({
    mutationKey: ["confirmOrderItem", suppressBanner],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Confirming order item",
        buildLogContext(
          "orders",
          "confirm-order-item",
          userId,
          companyId,
          undefined,
          {
            orderId: input.orderId,
            productItemId: input.productItemId,
            quantity: input.quantity
          }
        )
      );

      try {
        const response: ConfirmOrderItemResponse = await fetchGraphQL(
          confirmOrderItemMutation,
          { input },
          setAlert
        );
        const { scan, code } = response.confirmOrderItem;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Confirm order item rejected by API",
            buildLogContext(
              "orders",
              "confirm-order-item",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                orderId: input.orderId
              }
            )
          );
          if (suppressBanner) {
            throw new OrderMutationRejectedError(String(code));
          }
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!scan) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Confirm order item OK but missing scan payload",
            buildLogContext(
              "orders",
              "confirm-order-item",
              userId,
              companyId,
              undefined,
              {
                orderId: input.orderId,
                productItemId: input.productItemId
              }
            )
          );
          if (suppressBanner) {
            throw new OrderMutationRejectedError("MISSING_SCAN_PAYLOAD");
          }
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        sendAppLog(
          LOG_LEVEL.INFO,
          "Order item confirmed successfully",
          buildLogContext(
            "orders",
            "confirm-order-item",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId,
              productItemId: input.productItemId
            }
          )
        );
        return scan;
      } catch (error) {
        if (error instanceof OrderMutationRejectedError) {
          throw error;
        }
        captureAppException(
          "Failed to confirm order item",
          error,
          buildLogContext(
            "orders",
            "confirm-order-item",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId
            }
          )
        );
        if (suppressBanner) {
          throw error instanceof Error ? error : new Error(String(error));
        }
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

// ─── Order Lifecycle Mutations ─────────────────────────────────────────────────

export const useCancelOrder = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const { dispatchDetail } = useOrderDispatchers();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Order | null, Error, CancelOrderInput>({
    mutationKey: ["cancelOrder"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Cancelling order",
        buildLogContext(
          "orders",
          "cancel-order",
          userId,
          companyId,
          undefined,
          {
            orderId: input.orderId,
            ...(input.cancelReason ? { cancelReason: input.cancelReason } : {})
          }
        )
      );

      try {
        const response: CancelOrderResponse = await fetchGraphQL(
          cancelOrderMutation,
          { input },
          setAlert
        );
        const { order, code } = response.cancelOrder;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Cancel order rejected by API",
            buildLogContext(
              "orders",
              "cancel-order",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!order) {
          captureAppException(
            "API returned OK but order object missing after cancelOrder",
            new Error(
              "API returned OK but order object missing after cancelOrder"
            ),
            buildLogContext(
              "orders",
              "cancel-order",
              userId,
              companyId,
              undefined,
              {
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatchDetail(order);
        sendAppLog(
          LOG_LEVEL.INFO,
          "Order cancelled successfully",
          buildLogContext(
            "orders",
            "cancel-order",
            userId,
            companyId,
            undefined,
            {
              orderId: order.orderId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });
        return order;
      } catch (error) {
        captureAppException(
          "Failed to cancel order",
          error,
          buildLogContext(
            "orders",
            "cancel-order",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

export const useAcceptOffer = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const { dispatchDetail } = useOrderDispatchers();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Order | null, Error, AcceptOfferInput>({
    mutationKey: ["acceptOffer"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Accepting order offer",
        buildLogContext(
          "orders",
          "accept-offer",
          userId,
          companyId,
          undefined,
          {
            orderId: input.orderId
          }
        )
      );

      try {
        const response: AcceptOfferResponse = await fetchGraphQL(
          acceptOfferMutation,
          { input },
          setAlert
        );
        const { order, code } = response.acceptOffer;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Accept offer rejected by API",
            buildLogContext(
              "orders",
              "accept-offer",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!order) {
          captureAppException(
            "API returned OK but order object missing after acceptOffer",
            new Error(
              "API returned OK but order object missing after acceptOffer"
            ),
            buildLogContext(
              "orders",
              "accept-offer",
              userId,
              companyId,
              undefined,
              {
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatchDetail(order);
        sendAppLog(
          LOG_LEVEL.INFO,
          "Order offer accepted successfully",
          buildLogContext(
            "orders",
            "accept-offer",
            userId,
            companyId,
            undefined,
            {
              orderId: order.orderId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });
        return order;
      } catch (error) {
        captureAppException(
          "Failed to accept offer",
          error,
          buildLogContext(
            "orders",
            "accept-offer",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

/**
 * Updates sales order status via GraphQL `updateOrderStatus` (e.g. OFFER_ACCEPTED after confirm).
 */
export const useUpdateSalesOrderStatus = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const { dispatchDetail } = useOrderDispatchers();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Order | null, Error, UpdateOrderStatusInput>({
    mutationKey: ["updateSalesOrderStatus"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Updating sales order status",
        buildLogContext(
          "orders",
          "update-order-status",
          userId,
          companyId,
          undefined,
          {
            orderId: input.orderId,
            status: input.status
          }
        )
      );

      try {
        const response: UpdateOrderStatusResponse = await fetchGraphQL(
          updateOrderStatusMutation,
          { input },
          setAlert
        );
        const { order, code } = response.updateOrderStatus;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "updateOrderStatus rejected by API",
            buildLogContext(
              "orders",
              "update-order-status",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!order) {
          captureAppException(
            "API returned OK but order object missing after updateOrderStatus",
            new Error(
              "API returned OK but order object missing after updateOrderStatus"
            ),
            buildLogContext(
              "orders",
              "update-order-status",
              userId,
              companyId,
              undefined,
              {
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatchDetail(order);
        sendAppLog(
          LOG_LEVEL.INFO,
          "Sales order status updated",
          buildLogContext(
            "orders",
            "update-order-status",
            userId,
            companyId,
            undefined,
            {
              orderId: order.orderId,
              status: order.status
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });
        return order;
      } catch (error) {
        captureAppException(
          "Failed to update sales order status",
          error,
          buildLogContext(
            "orders",
            "update-order-status",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

export const useCompleteOrder = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const { dispatchDetail } = useOrderDispatchers();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Order | null, Error, CompleteOrderInput>({
    mutationKey: ["completeOrder"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Completing order",
        buildLogContext(
          "orders",
          "complete-order",
          userId,
          companyId,
          undefined,
          {
            orderId: input.orderId
          }
        )
      );

      try {
        const response: CompleteOrderResponse = await fetchGraphQL(
          completeOrderMutation,
          { input },
          setAlert
        );
        const { order, code } = response.completeOrder;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Complete order rejected by API",
            buildLogContext(
              "orders",
              "complete-order",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!order) {
          captureAppException(
            "API returned OK but order object missing after completeOrder",
            new Error(
              "API returned OK but order object missing after completeOrder"
            ),
            buildLogContext(
              "orders",
              "complete-order",
              userId,
              companyId,
              undefined,
              {
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatchDetail(order);
        sendAppLog(
          LOG_LEVEL.INFO,
          "Order completed successfully",
          buildLogContext(
            "orders",
            "complete-order",
            userId,
            companyId,
            undefined,
            {
              orderId: order.orderId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });
        return order;
      } catch (error) {
        captureAppException(
          "Failed to complete order",
          error,
          buildLogContext(
            "orders",
            "complete-order",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

export const useUpdateOrderCustomer = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const { dispatchDetail } = useOrderDispatchers();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Order | null, Error, UpdateOrderCustomerInput>({
    mutationKey: ["updateOrderCustomer"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Updating order customer",
        buildLogContext(
          "orders",
          "update-order-customer",
          userId,
          companyId,
          storeId,
          {
            orderId: input.orderId,
            customerId: input.customerId ?? "none"
          }
        )
      );

      try {
        const response: UpdateOrderCustomerResponse = await fetchGraphQL(
          updateOrderCustomerMutation,
          { input },
          setAlert
        );
        const { order, code } = response.updateOrderCustomer;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "updateOrderCustomer rejected by API",
            buildLogContext(
              "orders",
              "update-order-customer",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(code),
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          throw new HandledApiError(
            `updateOrderCustomer rejected by API: ${code}`
          );
        }

        if (!order) {
          const err = new HandledApiError(
            "API returned OK but order object missing after updateOrderCustomer"
          );
          captureAppException(
            "API returned OK but order object missing after updateOrderCustomer",
            err,
            buildLogContext(
              "orders",
              "update-order-customer",
              userId,
              companyId,
              storeId,
              {
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          throw err;
        }

        dispatchDetail(order);
        sendAppLog(
          LOG_LEVEL.INFO,
          "Order customer updated successfully",
          buildLogContext(
            "orders",
            "update-order-customer",
            userId,
            companyId,
            storeId,
            {
              orderId: order.orderId,
              customerId: order.customerId ?? "none"
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });
        return order;
      } catch (error) {
        if (error instanceof HandledApiError) {
          throw error; // alert already set — don't overwrite it
        }
        captureAppException(
          "Failed to update order customer",
          error,
          buildLogContext(
            "orders",
            "update-order-customer",
            userId,
            companyId,
            storeId,
            {
              orderId: input.orderId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw error instanceof Error ? error : new Error(String(error));
      }
    }
  });
};
